import React, { useState, useEffect, useCallback } from "react";
import AppDialog, { DialogCloseButton } from "../../../components/dialog/Dialog";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Typography } from "@progress/kendo-react-common";
import { FaWhatsapp, FaEnvelope, FaCopy } from "react-icons/fa";
import IconComponent from "../../../components/common/IconComponent";
import { convertHtmlToWhatsAppFormat, copyHtmlToClipboard, formatIndianNumber } from "../../../_helper/helper";
import { SuccessToast } from "../../../components/toast/Toasts";
import moment from "moment";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { downloadB2BQuotation } from "../services/b2bQuotation.services";
import { FaDownload } from "react-icons/fa";

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const renderWhatsAppPreview = (text: string) => {
  if (!text) return "";
  let html = text
    .replace(/\*([^*]+)\*/g, "<strong>$1</strong>") // Bold: *text*
    .replace(/_([^_]+)_/g, "<em>$1</em>")        // Italic: _text_
    .replace(/\n/g, "<br />");                  // Newlines
  return html;
};

interface ShareB2BQuotationDialogProps {
  data: any;
}

const ShareB2BQuotationDialog: React.FC<ShareB2BQuotationDialogProps> = ({ data }) => {
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const open = dialogName === "shareB2BQuotation";

  const [selectedTab, setSelectedTab] = useState(0);
  const [options, setOptions] = useState({
    hideTotalPrice: false,
    showTransport: true,
    showItinerary: true,
    showInclusions: true,
    showTerms: true,
  });

  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [downloading, setDownloading] = useState(false);
  const dispatch = useAppDispatch();

  const handleDownload = async () => {
    if (data?.ID) {
      setDownloading(true);
      try {
        const res = await dispatch(downloadB2BQuotation(Number(data.ID)));
        if (res && downloadB2BQuotation.fulfilled.match(res)) {
          const blob = res.payload as Blob;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Quotation-${data?.QuotationReference || data?.ID}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Download error:", error);
      } finally {
        setDownloading(false);
      }
    }
  };

  const handleTabSelect = (e: any) => {
    setSelectedTab(e.selected);
  };

  const generateContent = useCallback(() => {
    if (!data) return;

    const company = data.CompanyDetails || {};
    const tripName = data.TripDestinationName || "Your Trip";
    const duration = `${data.Nights} Nights / ${data.Nights + 1} Days`;
    const travelerInfo = `${data.Adults} Adults${data.Children > 0 ? `, ${data.Children} Child` : ""}${data.Infants > 0 ? `, ${data.Infants} Infant` : ""}`;
    const userName = data.CreatedByName || "";

    const sortedTransRows: any[] = [];
    (data.TransportsAndActivities?.ServiceGroups || []).forEach((group: any) => {
      (group.Services || []).forEach((svc: any) => {
        (svc.Subservices || []).forEach((sub: any) => {
          (sub.DailyPrices || []).forEach((dp: any, dpIdx: number) => {
            sortedTransRows.push({
              day: dp.Day || dp.DayNo || (dpIdx + 1),
              name: svc.ServiceName || svc.Name || "-",
              type: "MAIN",
              svcType: svc.Type,
              sub: sub.TypeName,
              qty: sub.Qty,
              slot: svc.Slot,
              dur: svc.DurationMinutes,
              price: (dp.GivenPrice || 0) * (sub.Qty || 1),
              date: dp.Date,
              svc: svc
            });
          });
        });
      });
    });

    (data.TransportsAndActivities?.ExtraTransportationServices || []).forEach((e: any) => {
      sortedTransRows.push({ day: e.DayNo || e.Day, name: e.ServiceName, type: "EXTRA", price: e.TotalPrice });
    });

    (data.OtherSpecialServices || []).forEach((os: any) => {
      sortedTransRows.push({ day: os.DayNo || os.Day, name: os.ServiceName, type: "SPECIAL", price: os.TotalPrice });
    });
    sortedTransRows.sort((a, b) => a.day - b.day);

    let msg = `Hi ${data.FirstName || "Valued Customer"},\n\n`;
    msg += `Greetings from *${company.CompanyName || "AICRM"}*.\n\n`;
    msg += `Thank you for your query with us. As per your requirements, following are the package details.\n\n`;

    msg += `*Trip ID ${data.QuotationReference || `#${data.ID}`}* _(${(data.PackageOptions || []).length} Package Category/Options)_\n`;
    msg += `----------\n`;

    (data.PackageOptions || []).forEach((opt: any, idx: number) => {
      msg += `⏬ *OPTION ${idx + 1}: ${opt.OptionName}*\n\n`;
      msg += `• *${moment(data.TripStartDate).format("DD MMM, YYYY")}* _for_ *${data.Nights} Nights, ${data.Nights + 1} Days*\n`;
      msg += `• *${travelerInfo}*\n\n`;

      if (!options.hideTotalPrice) {
        msg += `*Total Price (INR): ${formatIndianNumber(opt.GrandTotal)} /-*\n\n`;
      }

      msg += `🏨  *_Hotels & Inclusions_*\n`;
      msg += `-----------\n`;

      const hotelNights = (opt.Hotels || []).flatMap((h: any) =>
        (h.NightPrices || []).map((np: any) => ({ ...h, ...np, detailType: 'HOTEL' }))
      );
      const inclusions = (opt.HotelSpecialInclusions || []).map((incl: any) => ({
        ...incl,
        detailType: 'INCLUSION'
      }));

      const accommodationRows = [...hotelNights, ...inclusions].sort((a, b) => {
        const diff = Number(a.Night || 0) - Number(b.Night || 0);
        if (diff !== 0) return diff;
        if (a.detailType === 'HOTEL' && b.detailType === 'INCLUSION') return -1;
        if (a.detailType === 'INCLUSION' && b.detailType === 'HOTEL') return 1;
        return 0;
      });

      accommodationRows.forEach((row: any, rIdx: number) => {
        const isSameNight = rIdx > 0 && Number(accommodationRows[rIdx - 1].Night) === Number(row.Night);

        if (row.detailType === 'HOTEL') {
          if (!isSameNight) {
            msg += `*${getOrdinal(row.Night)} Night* _at_ *${row.HotelName || "-"}*\n`;
            msg += `_Check-in: ${moment(row.Date).format("DD MMM")}_ & _Check-out: ${moment(row.Date).add(1, "day").format("DD MMM")}_ ${row.HotelCategory ? `(${row.HotelCategory}★)` : ""}\n`;
          } else {
            msg += `*${row.HotelName || "-"}* ${row.HotelCategory ? `(${row.HotelCategory}★)` : ""}\n`;
          }
          msg += `${row.MealPlanName || "-"} • ${row.NoOfRooms || 1} ${row.RoomTypeName || "-"}\n`;
          msg += `(${data.Adults} Pax ${data.Children > 0 ? `+ ${data.Children} Child` : ""})\n\n`;
        } else {
          if (!isSameNight) {
            msg += `*${getOrdinal(row.Night)} Night Inclusion*:\n`;
          }
          msg += `• *${row.ServiceName || row.Service || "-"}* _(INR ${formatIndianNumber(row.TotalPrice)})_\n\n`;
        }
      });
    });

    msg += `-------\n`;
    msg += `⏩ *For All Options*\n`;
    msg += `Details below are applicable for all the options.\n`;
    msg += `-------\n\n`;

    if (options.showTransport) {
      const mainServices = sortedTransRows.filter(r => r.type === "MAIN");
      if (mainServices.length > 0) {
        msg += `🚖  *Transportation and Activities*\n`;
        msg += `-----------\n`;
        let lastDayInMsg = -1;
        mainServices.forEach(r => {
          if (r.day !== lastDayInMsg) {
            msg += `*Day ${r.day} - ${moment(r.date || data.TripStartDate).add(r.day - 1, 'days').format("ddd, Do MMM'YY")}*\n`;
          }
          msg += `• ${r.name} _(${r.qty}-${r.sub || (r.svc?.Type || 'Service')})_\n`;
          if (r.svc?.Comment) msg += `  _(Note: ${r.svc.Comment})_\n`;
          msg += `\n`;
          lastDayInMsg = r.day;
        });
      }

      const extraTrans = sortedTransRows.filter(r => r.type === "EXTRA" || r.type === "SPECIAL");
      if (extraTrans.length > 0) {
        msg += `*Extra Transportation & Special Services*\n`;
        msg += `-------\n`;
        extraTrans.forEach(r => {
          msg += `*Day ${r.day}* - *${r.name}* _(INR ${formatIndianNumber(r.price)})_\n`;
        });
        msg += `\n`;
      }
    } else {
      const specialOnly = sortedTransRows.filter(r => r.type === "SPECIAL");
      if (specialOnly.length > 0) {
        msg += `*Special Services*\n`;
        msg += `-------\n`;
        specialOnly.forEach(r => {
          msg += `*Day ${r.day}* - *${r.name}* _(INR ${formatIndianNumber(r.price)})_\n`;
        });
        msg += `\n`;
      }
    }

    if (options.showInclusions && (data.Inclusion || data.Exclusion)) {
      msg += `📋  *_Inclusions & Exclusions_*\n`;
      msg += `-------\n`;
      if (data.Inclusion) msg += `*✅ Inclusions:*\n` + convertHtmlToWhatsAppFormat(data.Inclusion) + `\n\n`;
      if (data.Exclusion) msg += `*❌ Exclusions:*\n` + convertHtmlToWhatsAppFormat(data.Exclusion) + `\n\n`;
    }

    if (options.showItinerary && data.Itinary?.length > 0) {
      msg += `🗓️   *_Day Wise Itinerary_*\n`;
      msg += `----------\n`;
      data.Itinary.forEach((it: any) => {
        msg += `*Day ${it.DayNo} - ${moment(data.TripStartDate).add(it.DayNo - 1, 'days').format("dddd Do MMM, YYYY")}*\n`;
        msg += `*${it.ItineraryTitle || "Sightseeing / Transfer"}*\n`;
        msg += convertHtmlToWhatsAppFormat(it.Description) + `\n\n`;
      });
    }

    if (options.showTerms && data.TermsConditions) {
      msg += `*📜 Terms & Conditions*\n` + convertHtmlToWhatsAppFormat(data.TermsConditions) + `\n`;
    }

    msg += `\nBest Regards,`;
    if (userName) msg += `\n*${userName}*`;
    msg += `\n*${company.CompanyName || ""}*\n${company.CompanyPhone || ""}`;
    setWhatsappMessage(msg);

    const primaryBlue = "#1e3a8a";
    const lightBlue = "#f0f7ff";
    const borderGray = "#e2e8f0";
    const textDark = "#1e293b";
    const textMuted = "#64748b";

    let html = `
      <div style="font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: ${textDark}; margin: 0;">
        <div style="max-width: 750px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
          
          <div style="padding: 30px; text-align: center; border-bottom: 1px solid ${borderGray};">
            ${company.CompanyLogo ? `<img src="${company.CompanyLogo}" alt="${company.CompanyName}" style="max-height: 70px; margin-bottom: 15px;">` : `<h1 style="color: ${primaryBlue}; margin: 0; font-size: 28px;">${company.CompanyName || "AICRM"}</h1>`}
            <h2 style="color: ${primaryBlue}; margin: 10px 0 0 0; font-size: 20px;">Greetings from ${company.CompanyName || "AICRM"}!</h2>
          </div>

          <div style="padding: 25px;">
            <p style="margin: 0 0 20px 0; font-size: 15px;">Hi <strong>${data.FirstName || "Valued Customer"}</strong>,</p>
            <p style="margin: 0 0 20px 0; font-size: 15px; line-height: 1.6;">Thank you for your query with us. Detailed below are the proposed travel options for your upcoming trip to <strong>${tripName}</strong>.</p>
            
            <div style="background-color: ${lightBlue}; padding: 20px; border-radius: 8px; border-left: 5px solid ${primaryBlue};">
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 5px 0;"><span style="color: ${textMuted}; font-size: 12px; text-transform: uppercase; font-weight: 700;">Trip ID:</span></td>
                  <td style="padding: 5px 0;"><strong>${data.QuotationReference || `#${data.ID}`}</strong></td>
                  <td style="padding: 5px 0;"><span style="color: ${textMuted}; font-size: 12px; text-transform: uppercase; font-weight: 700;">Travel Dates:</span></td>
                  <td style="padding: 5px 0;"><strong>${moment(data.TripStartDate).format("DD MMM'YY")} - ${moment(data.TripStartDate).add(data.Nights, "days").format("DD MMM'YY")}</strong></td>
                </tr>
                <tr>
                  <td style="padding: 5px 0;"><span style="color: ${textMuted}; font-size: 12px; text-transform: uppercase; font-weight: 700;">Duration:</span></td>
                  <td style="padding: 5px 0;"><strong>${duration}</strong></td>
                  <td style="padding: 5px 0;"><span style="color: ${textMuted}; font-size: 12px; text-transform: uppercase; font-weight: 700;">Passengers:</span></td>
                  <td style="padding: 5px 0;"><strong>${travelerInfo}</strong></td>
                </tr>
              </table>
            </div>
          </div>

          <div style="padding: 0 25px;">
            <h3 style="color: ${primaryBlue}; border-bottom: 2px solid ${primaryBlue}; padding-bottom: 8px; margin: 30px 0 20px 0; font-size: 18px; text-transform: uppercase;">Package Options</h3>
    `;

    (data.PackageOptions || []).forEach((opt: any, idx: number) => {
      html += `
        <div style="margin-bottom: 40px; border: 1px solid ${borderGray}; border-radius: 10px; overflow: hidden;">
          <div style="background-color: ${primaryBlue}; color: #ffffff; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 16px; font-weight: 700;">OPTION ${idx + 1}: ${opt.OptionName}</span>
            ${!options.hideTotalPrice ? `<span style="font-size: 18px; font-weight: 800;">INR ${formatIndianNumber(opt.GrandTotal)}</span>` : ""}
          </div>
          
          <div style="padding: 20px;">
            <h4 style="margin: 0 0 10px 0; color: ${primaryBlue}; font-size: 12px; text-transform: uppercase;">🏨 Accommodation & Inclusions</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
              <thead style="background-color: #f8fafc;">
                <tr>
                  <th style="padding: 10px; border-bottom: 2px solid ${borderGray}; text-align: left;">Night</th>
                  <th style="padding: 10px; border-bottom: 2px solid ${borderGray}; text-align: left;">Destination & Hotel / Service</th>
                  <th style="padding: 10px; border-bottom: 2px solid ${borderGray}; text-align: left;">Details</th>
                  <th style="padding: 10px; border-bottom: 2px solid ${borderGray}; text-align: right;">Cost / Qty</th>
                </tr>
              </thead>
              <tbody>
      `;

      const hotelNightsEmail = (opt.Hotels || []).flatMap((h: any) =>
        (h.NightPrices || []).map((np: any) => ({ ...h, ...np, detailType: 'HOTEL' }))
      );
      const inclusionsEmail = (opt.HotelSpecialInclusions || []).map((incl: any) => ({
        ...incl,
        detailType: 'INCLUSION'
      }));

      const accommodationRowsEmail = [...hotelNightsEmail, ...inclusionsEmail].sort((a, b) => {
        const diff = Number(a.Night || 0) - Number(b.Night || 0);
        if (diff !== 0) return diff;
        if (a.detailType === 'HOTEL' && b.detailType === 'INCLUSION') return -1;
        if (a.detailType === 'INCLUSION' && b.detailType === 'HOTEL') return 1;
        return 0;
      });

      accommodationRowsEmail.forEach((row: any, rIdx: number) => {
        const isSameNight = rIdx > 0 && Number(accommodationRowsEmail[rIdx - 1].Night) === Number(row.Night);

        if (row.detailType === 'HOTEL') {
          html += `
            <tr style="border-bottom: 1px solid #f1f5f9;">
              <td style="padding: 12px 10px; font-weight: 700; vertical-align: top;">${!isSameNight ? `${getOrdinal(row.Night)}` : ""}</td>
              <td style="padding: 12px 10px;">
                <div style="font-weight: 700; color: ${textDark}">
                  ${row.DestinationName || data.TripDestinationName || "Destination"} 
                  ${row.HotelCategory ? `<span style="font-size: 10px; background: #fff9e6; color: #b45309; padding: 1px 4px; border-radius: 3px; border: 1px solid #fde68a; margin-left: 5px;">${row.HotelCategory}★</span>` : ""}
                </div>
                <div style="font-size: 12px; color: ${textMuted}; margin-top: 2px;">${row.HotelName}</div>
                <div style="font-size: 11px; color: ${textMuted}; margin-top: 2px;">Check-in: ${moment(row.Date).format("DD MMM")}</div>
              </td>
              <td style="padding: 12px 10px;">
                <div style="font-weight: 700;">${row.RoomTypeName || "Room"}</div>
                <div style="display: inline-block; padding: 2px 6px; background-color: #e2e8f0; color: #475569; font-size: 10px; border-radius: 4px; margin-top: 4px;">${row.MealPlanName || "Room Only"}</div>
              </td>
              <td style="padding: 12px 10px; text-align: right; color: ${textMuted}; vertical-align: top;">
                ${row.NoOfRooms} Room(s)
              </td>
            </tr>
          `;
        } else {
          html += `
            <tr style="border-bottom: 1px solid #f1f5f9; background-color: #fffdfa;">
              <td style="padding: 12px 10px; font-weight: 700; color: #b45309; vertical-align: top;">${!isSameNight ? `Night ${row.Night}` : ""}</td>
              <td style="padding: 12px 10px;" colSpan="2">
                <div style="display: inline-block; padding: 1px 5px; background-color: #fef3c7; color: #92400e; font-size: 9px; border-radius: 3px; font-weight: 800; text-transform: uppercase;">Inclusion</div>
                <div style="font-weight: 700; color: #92400e; margin-top: 4px;">${row.ServiceName || row.Service}</div>
                ${row.Comment ? `<div style="font-size: 11px; color: #b45309; font-style: italic; margin-top: 2px;">Note: ${row.Comment}</div>` : ""}
              </td>
              <td style="padding: 12px 10px; text-align: right; font-weight: 700; color: #92400e; vertical-align: top;">
                ₹${formatIndianNumber(row.TotalPrice)}
              </td>
            </tr>
          `;
        }
      });

      html += `</tbody></table>`;

      html += `</div></div>`;
    });

    if (options.showTransport || sortedTransRows.some(r => r.type === "SPECIAL")) {
      html += `
        <div style="background-color: #f8fafc; padding: 25px; border-radius: 12px; margin-bottom: 30px; border: 1px solid ${borderGray};">
          <h3 style="color: ${primaryBlue}; margin: 0 0 20px 0; font-size: 16px; display: flex; align-items: center; gap: 8px;">⏩ Shared Trip Services <span style="font-size: 11px; font-weight: 500; color: ${textMuted};">(Applicable for all options)</span></h3>
      `;

      if (options.showTransport) {
        html += `
          <div style="margin-bottom: 25px;">
            <h4 style="font-size: 13px; color: ${textDark}; margin-bottom: 12px; padding-bottom: 5px; border-bottom: 1px solid #cbd5e1;">🚖 Transportation & Activities</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
        `;
        let lastDayEmail = -1;
        sortedTransRows.filter(r => r.type === "MAIN").forEach(r => {
          const isSameDay = r.day === lastDayEmail;
          html += `
            <tr style="border-bottom: 1px solid #edf2f7;">
              <td style="padding: 10px 0; width: 140px; vertical-align: top;">
                ${!isSameDay ? `<strong>Day ${r.day}</strong> <span style="color: ${textMuted}; font-size: 11px;">(${moment(r.date || data.TripStartDate).add(r.day - 1, 'days').format("ddd, DD MMM")})</span>` : ""}
              </td>
              <td style="padding: 10px; font-weight: 700; vertical-align: top;">${r.name || "-"}</td>
              <td style="padding: 10px; color: ${textMuted}; font-size: 12px; vertical-align: top;">${r.qty || 1} x ${r.sub || r.svcType || "-"}</td>
            </tr>
          `;
          lastDayEmail = r.day;
        });
        html += `</table></div>`;
      }

      const extrasForEmail = sortedTransRows.filter(r => {
        if (options.showTransport) return r.type === "EXTRA" || r.type === "SPECIAL";
        return r.type === "SPECIAL";
      });

      if (extrasForEmail.length > 0) {
        html += `
          <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 15px; margin-top: 15px;">
            <h4 style="font-size: 12px; color: #166534; text-transform: uppercase; margin: 0 0 10px 0;">
              ${options.showTransport ? '🎁 Extras & Special Services' : '🎁 Special Services'}
            </h4>
            <ul style="margin: 0; padding-left: 15px; font-size: 13px; color: #166534; list-style-type: none;">
        `;
        extrasForEmail.forEach(r => {
          html += `<li style="margin-bottom: 6px;">• <strong>Day ${r.day}</strong>: ${r.name} <span style="opacity: 0.8; font-weight: bold;">(INR ${formatIndianNumber(r.price)})</span></li>`;
        });
        html += `</ul></div>`;
      }

      html += `</div>`;
    }

    if (options.showInclusions && (data.Inclusion || data.Exclusion)) {
      html += `
        <div style="display: table; width: 100%; border-collapse: separate; border-spacing: 15px 0; margin-bottom: 30px;">
          <div style="display: table-cell; width: 50%; vertical-align: top; background-color: #f0fdf4; padding: 15px; border-radius: 8px; border: 1px solid #dcfce7;">
            <h4 style="margin: 0 0 10px 0; color: #166534; font-size: 13px; text-transform: uppercase;">✅ Inclusions</h4>
            <div style="font-size: 13px; color: #166534; line-height: 1.5;">${data.Inclusion || "As per details above"}</div>
          </div>
          <div style="display: table-cell; width: 50%; vertical-align: top; background-color: #fef2f2; padding: 15px; border-radius: 8px; border: 1px solid #fee2e2;">
            <h4 style="margin: 0 0 10px 0; color: #991b1b; font-size: 13px; text-transform: uppercase;">❌ Exclusions</h4>
            <div style="font-size: 13px; color: #991b1b; line-height: 1.5;">${data.Exclusion || "Anything not mentioned in inclusions"}</div>
          </div>
        </div>
      `;
    }

    if (options.showItinerary && data.Itinary?.length > 0) {
      html += `
        <div style="padding: 0 25px 25px 25px;">
          <h3 style="color: ${primaryBlue}; border-bottom: 2px solid ${primaryBlue}; padding-bottom: 8px; margin: 30px 0 20px 0; font-size: 18px; text-transform: uppercase;">🗓️ Day-wise Itinerary</h3>
      `;
      data.Itinary.forEach((it: any) => {
        html += `
          <div style="margin-bottom: 25px; background: #ffffff; border: 1px solid ${borderGray}; border-radius: 8px; overflow: hidden;">
            <div style="background-color: #f1f5f9; padding: 10px 15px; font-weight: 700; color: ${primaryBlue};">
              Day ${it.DayNo}: ${moment(data.TripStartDate).add(it.DayNo - 1, 'days').format("dddd, Do MMM YYYY")}
            </div>
            <div style="padding: 15px;">
              <div style="font-weight: 800; color: ${textDark}; margin-bottom: 8px; font-size: 15px;">${it.ItineraryTitle || "Sightseeing / Transfer"}</div>
              <div style="font-size: 14px; line-height: 1.6; color: ${textDark}">${it.Description}</div>
            </div>
          </div>
        `;
      });
      html += `</div>`;
    }

    if (options.showTerms && data.TermsConditions) {
      html += `
        <div style="padding: 0 25px 25px 25px; border-top: 1px solid ${borderGray}; margin-top: 20px; pt-20">
          <h4 style="font-size: 13px; color: ${textMuted}; text-transform: uppercase; margin: 20px 0 10px 0;">📜 Terms & Conditions</h4>
          <div style="font-size: 12px; color: ${textMuted}; line-height: 1.6;">${data.TermsConditions}</div>
        </div>
      `;
    }

    html += `
          </div>

          <!-- Footer / Signature -->
          <div style="padding: 30px; border-top: 1px solid ${borderGray}; background-color: #f8fafc; text-align: left;">
            <p style="margin: 0; color: ${textMuted}; font-size: 14px;">Best Regards,</p>
            ${userName ? `<p style="margin: 5px 0 0 0; color: ${primaryBlue}; font-size: 16px; font-weight: 700;">${userName}</p>` : ""}
            <p style="margin: 2px 0 0 0; color: ${textDark}; font-size: 14px; font-weight: 600;">${company.CompanyName}</p>
            ${company.CompanyEmail ? `<p style="margin: 8px 0 0 0; color: ${textMuted}; font-size: 12px;">📧 ${company.CompanyEmail}</p>` : ""}
            ${company.CompanyPhone ? `<p style="margin: 4px 0 0 0; color: ${textMuted}; font-size: 12px;">📞 ${company.CompanyPhone}</p>` : ""}
          </div>
        </div>
      </div>
    `;

    setEmailHtml(html);
  }, [data, options]);

  useEffect(() => {
    if (open && data) {
      generateContent();
    }
  }, [open, data, generateContent]);

  const handleCopy = async () => {
    if (selectedTab === 0) {
      // WhatsApp
      await navigator.clipboard.writeText(whatsappMessage);
      SuccessToast("Message copied for WhatsApp!");
    } else {
      // Email
      await copyHtmlToClipboard(emailHtml);
      SuccessToast("Template copied for Email!");
    }
  };

  const handleWhatsAppShare = () => {
    const encodedText = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedText}`;
    window.open(whatsappUrl, "_blank");
  };

  if (!open) return null;

  return (
    <AppDialog className="share-quotation-dialog">
      <div style={{ minWidth: 850, padding: "10px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <Typography.h5 className="fw-bold m-0">Share B2B Quotation</Typography.h5>
            <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1" style={{ fontSize: "11px", fontWeight: 700 }}>
              {data?.QuotationReference || `#${data?.ID}`}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-success text-white fw-bold d-flex align-items-center gap-2 px-3"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? <span className="spinner-border spinner-border-sm"></span> : <IconComponent icon={FaDownload} />}
              Download PDF
            </button>
            <DialogCloseButton />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12 d-flex gap-3 flex-wrap">
            <Checkbox
              label="Hide Total"
              checked={options.hideTotalPrice}
              onChange={(e) => setOptions({ ...options, hideTotalPrice: e.value })}
            />
            <Checkbox
              label="Transport & Activities"
              checked={options.showTransport}
              onChange={(e) => setOptions({ ...options, showTransport: e.value })}
            />
            <Checkbox
              label="Itinerary"
              checked={options.showItinerary}
              onChange={(e) => setOptions({ ...options, showItinerary: e.value })}
            />
            <Checkbox
              label="Policies"
              checked={options.showInclusions}
              onChange={(e) => setOptions({ ...options, showInclusions: e.value })}
            />
            <Checkbox
              label="Terms"
              checked={options.showTerms}
              onChange={(e) => setOptions({ ...options, showTerms: e.value })}
            />
          </div>
        </div>

        <TabStrip selected={selectedTab} onSelect={handleTabSelect}>
          <TabStripTab
            title={
              <div className="d-flex align-items-center gap-2">
                <IconComponent icon={FaWhatsapp} style={{ color: "#25D366" }} /> WhatsApp
              </div>
            }
          >
            <div className="mt-3">
              <div
                className="whatsapp-chat-container"
                style={{
                  height: "500px",
                  overflowY: "auto",
                  backgroundColor: "#e5ddd5",
                  padding: "20px",
                  borderRadius: "8px",
                  backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcd2ad1.png')", // WhatsApp wallpaper
                  backgroundRepeat: "repeat",
                  maxWidth: 800
                }}
              >
                <div
                  className="whatsapp-bubble"
                  style={{
                    backgroundColor: "#dcf8c6",
                    borderRadius: "12px",
                    padding: "12px 16px",
                    position: "relative",
                    fontSize: "14px",
                    color: "#111b21",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    lineHeight: "1.5",
                    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                    maxWidth: "70%",
                    wordBreak: "break-word",
                    marginLeft: "auto"
                  }}
                  dangerouslySetInnerHTML={{ __html: renderWhatsAppPreview(whatsappMessage) }}
                />
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button themeColor="primary" fillMode="outline" onClick={handleCopy}>
                  <IconComponent icon={FaCopy} className="me-2" /> Copy Message
                </Button>
                <Button themeColor="success" onClick={handleWhatsAppShare} style={{ backgroundColor: "#25D366", borderColor: "#25D366", color: "white" }}>
                  <IconComponent icon={FaWhatsapp} className="me-2" /> Send via WhatsApp
                </Button>
              </div>
            </div>
          </TabStripTab>
          <TabStripTab
            title={
              <div className="d-flex align-items-center gap-2">
                <IconComponent icon={FaEnvelope} style={{ color: "#EA4335" }} /> Email
              </div>
            }
          >
            <div className="mt-3">
              <div
                className="email-preview border p-3"
                style={{ height: "500px", overflowY: "auto", backgroundColor: "#e1e1e1e3", borderRadius: "4px" }}
                dangerouslySetInnerHTML={{ __html: emailHtml }}
              />
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button themeColor="primary" fillMode="outline" onClick={handleCopy}>
                  <IconComponent icon={FaCopy} className="me-2" /> Copy for Email
                </Button>
              </div>
            </div>
          </TabStripTab>
        </TabStrip>
      </div>
    </AppDialog>
  );
};


export default ShareB2BQuotationDialog;
